import { Injectable, OnDestroy } from '@angular/core'
import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { BehaviorSubject, Observable, of, Subject } from 'rxjs'
import {
  AggregatedInventoryByMaterialNumber,
  InventoryItem,
  LotNumberAggregation,
  PagedResponse,
  StockListApiResponse
} from '../types/mpup-types'
import { InitialPaginationModel, MpupPaginationService } from './mpup-pagination.service'
import { OccEndpointsService } from '@spartacus/core'
import { catchError, finalize, map, shareReplay, takeUntil } from 'rxjs/operators'
import { MpupAuthorizationService } from './mpup-authorization.service'
import { environment } from '../../../../environments/environment'

export const MPUP_INVENTORY_ENDPOINT = environment.mpup.baseUrl + '/MPUP/stocklist'

@Injectable({
  providedIn: 'root'
})
export class MpupInventoryService extends MpupPaginationService implements OnDestroy {

  private listItems$ = new BehaviorSubject<ReadonlyArray<InventoryItem>>([])
  private loading$ = new BehaviorSubject(false)

  private currentFridgeId!: string
  private unsubscribe$ = new Subject<void>()
  /**
   * string value - url to the datasheet
   * null - no datasheet is available
   * undefined - no value loaded yet
   */
  private dataSheetUrls$ = new BehaviorSubject(new Map<string, string | null | undefined>())
  private urlObservable$ = this.dataSheetUrls$.asObservable()

  constructor (private http: HttpClient,
              private occEndpoints: OccEndpointsService,
              private authorizationService: MpupAuthorizationService) {
    super()
  }

  ngOnDestroy () {
    this.unsubscribe$.next()
    this.unsubscribe$.complete()
  }

  getLoadingState (): Observable<boolean> {
    return this.loading$.asObservable()
  }

  fetchInventoryItems (fridgeId: string): void {
    if (!fridgeId) {
      return
    }

    this.loading$.next(true)

    this.authorizationService.authorizedRequest<StockListApiResponse>(MPUP_INVENTORY_ENDPOINT, {
      params: { fridgeId, runMode: 4 }
    })
      .pipe(
        takeUntil(this.unsubscribe$),
        map((response) => response.stockData.item),
        map((items) => (
            items.map(item => (
              {
                ...item,
                materialNumber: this.formatMaterialNumber(item.materialNumber),
                targetQuantity: Number(item.targetQuantity),
                currentQuantity: Number(item.currentQuantity)
              })
            )
          )
        ),
        map((items) => items.sort((i1, i2) => i1.materialNumber.localeCompare(i2.materialNumber))),
        shareReplay(1),
        finalize(() => this.loading$.next(false)),
        catchError(e => this.handleError(e))
      )
      .subscribe({
        next: (items) => this.listItems$.next(items)
      })
  }

  private formatMaterialNumber (matNr: string): string {
    if (matNr.length < 18) {
      console.warn('Parsing of material number failed. The material number is not in the common format expected from the API.')
      return matNr
    }
    return `${matNr.substring(9, 12)}-${matNr.substring(12, 15)}-${matNr.substring(15, 18)}`
  }

  getDataSheetUrl (catalogNo: string): Observable<string | null | undefined> {
    if (!this.dataSheetUrls$.getValue().has(catalogNo)) {
      const value = this.dataSheetUrls$.getValue()
      value.set(catalogNo, undefined) // prevent request executing multiple times

      this.fetchDataSheetUrl(catalogNo)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: (url) => {
            value.set(catalogNo, url)
            this.dataSheetUrls$.next(value)
          }
        })
    }

    return this.urlObservable$.pipe(map((cache) => cache.get(catalogNo)))
  }

  private fetchDataSheetUrl (catalogNo: string): Observable<string | null> {
    return this.http.get(this.occEndpoints.buildUrl('productByOriginalCode', {
        urlParams: { productCode: catalogNo }, queryParams: { fields: 'dataSheets' }
      })
    )
      .pipe(
        map((data: any) => data.dataSheets.entry[0].value),
        map((link: string) => new DOMParser().parseFromString(link, 'text/html')),
        map((dom: Document) => dom.getElementsByTagName('a')[0]),
        map((elem: HTMLElement) => elem.getAttribute('href')),
        catchError((e: Error) => {
          console.warn('Error parsing datasheet url', e)
          return of(null)
        })
      )
  }

  getInventoryList (searchTerm: string | undefined,
                   pagination: InitialPaginationModel,
                   fridgeId: string): Observable<PagedResponse<AggregatedInventoryByMaterialNumber>> {
    if (!this.currentFridgeId || this.currentFridgeId !== fridgeId) {
      this.currentFridgeId = fridgeId
      this.fetchInventoryItems(fridgeId)
    }

    return this.listItems$
      .pipe(
        map((items): ReadonlyArray<InventoryItem> => {
          if (searchTerm) {
            return items.filter((inventoryItem) =>
              inventoryItem.materialDescription.toLowerCase().includes(searchTerm.toLowerCase()) ||
              inventoryItem.materialNumber.toLowerCase().includes(searchTerm.toLowerCase())
            )
          }
          return items
        }),
        map((items) => {
          const aggregatedResult = this.aggregateByLotNumberAndExpiryDate(items)

          return {
            pagination: {
              ...pagination,
              totalPages: Math.ceil(aggregatedResult.length / pagination.pageSize),
              totalResults: aggregatedResult.length
            },
            items: this.pageEntries(aggregatedResult, pagination)
          }
        })
      )
  }

  private aggregateByProperty (property: keyof InventoryItem, array: ReadonlyArray<InventoryItem>) {
    return array.reduce((acc, cur) => {
      if (!acc.hasOwnProperty(String(cur[property]))) {
        acc[String(cur[property])] = [cur]
      } else {
        acc[String(cur[property])].push(cur)
      }
      return acc
    }, ({} as { [key: string]: Array<InventoryItem> }))
  }

  private aggregateByLotNumberAndExpiryDate (array: ReadonlyArray<InventoryItem>): ReadonlyArray<AggregatedInventoryByMaterialNumber> {
    const aggregatedByMaterialNumber = this.aggregateByProperty('materialNumber', array)

    const aggregated = []
    for(const [matNr, itemsOfMatNr] of Object.entries(aggregatedByMaterialNumber)) {
      const aggregatedByBatchNumber = this.aggregateByProperty('batchNumber', itemsOfMatNr)

      const materialCollection: Array<LotNumberAggregation> = []
      for(const [batchNumber, itemsOfBatchNr] of Object.entries(aggregatedByBatchNumber)) {
        const aggregatedByDate = this.aggregateByProperty('expiryDate', itemsOfBatchNr)

        Object.entries(aggregatedByDate).forEach(([expiryDate, inventoryItems]) => {
          const availableItems = inventoryItems.filter(item => item.currentQuantity > 0)

          if(availableItems.length > 0) {
            const amount = availableItems.reduce((acc, cur) => acc + cur.currentQuantity, 0)
            materialCollection.push({ batchNumber, expiryDate,  amount })
          }
        })
      }
      const inventoryItem = array.find(i=> i.materialNumber === matNr)

      if(!inventoryItem) {
        continue
      }

      inventoryItem.totalQuantity = materialCollection.reduce((acc, cur) => acc + cur.amount, 0)

      aggregated.push({
        ...inventoryItem,
        materialCollection
      })
    }

    return aggregated
  }

  handleError (response: HttpErrorResponse): Observable<ReadonlyArray<InventoryItem>> {
    const errorCode = response.status
    const errorMessage = response.message || response.error.message
    console.error(`Http request failed. status: ${errorCode} - message: ${errorMessage}`)

    return of([])
  }
}
